import {
  BiAxisSide,
  BiLegendPosition,
  BiSeriesType,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
  BiGalleryVariant,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
  kGalleryMonths,
  kGalleryOrderStatuses,
  kGalleryServiceTypes,
} from './biGalleryFields';

const months = kGalleryMonths.slice(4);
const byType: Record<string, number[]> = {
  常规保养: [124300, 119800, 131200, 138600, 129900, 142700],
  钣金喷漆: [71900, 64400, 75100, 78300, 74200, 81600],
  轮胎更换: [42400, 41900, 45100, 47000, 44600, 48000],
};
const parts = [148200, 139600, 156300, 164100, 152800, 170400];
const labor = [90400, 86500, 95100, 99800, 95900, 101900];
const orders = [612, 584, 655, 689, 641, 712];

const stack: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '各服务类型月度营收',
  viz: {
    type: BiVizType.StackedBar,
    x: f.month.key,
    series: [{ key: f.revenue.key }],
    seriesBy: f.serviceType.key,
  },
  data: galleryReady(
    galleryResult(
      [f.month, f.serviceType, f.revenue],
      months.flatMap((month, i) =>
        kGalleryServiceTypes.map(type => [month, type, cents(byType[type][i])]),
      ),
    ),
  ),
};

const byStatus: Record<string, number[]> = {
  finished: [548, 521, 590, 617, 574, 640],
  inprogress: [31, 29, 33, 36, 32, 38],
  pending: [12, 15, 11, 14, 13, 16],
  cancelled: [21, 19, 21, 22, 22, 18],
};

/** A split by a coded dimension: codes in the rows, labels on screen. */
const byStatusCard: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '各状态月度工单数',
  viz: {
    type: BiVizType.StackedBar,
    x: f.month.key,
    series: [{ key: f.orders.key }],
    seriesBy: f.orderStatus.key,
  },
  data: galleryReady(
    galleryResult(
      [f.month, f.orderStatus, f.orders],
      months.flatMap((month, i) =>
        kGalleryOrderStatuses.map(status => [
          month,
          status,
          byStatus[status][i],
        ]),
      ),
    ),
  ),
};

const partsLaborResult = galleryResult(
  [f.month, f.partsRevenue, f.laborRevenue, f.orders],
  months.map((month, i) => [
    month,
    cents(parts[i]),
    cents(labor[i]),
    orders[i],
  ]),
);

const kPositionNames: Record<BiLegendPosition, string> = {
  [BiLegendPosition.Top]: '上方',
  [BiLegendPosition.Bottom]: '下方',
  [BiLegendPosition.Right]: '右侧',
};

function legendVariant(position: BiLegendPosition): BiGalleryVariant {
  return {
    key: `legend-${position}`,
    name: `图例${kPositionNames[position]}`,
    note: `legend.position = ${position}`,
    card: {
      ...stack,
      viz: { ...stack.viz, style: { legend: { show: true, position } } },
    },
  };
}

export const kStackedBarCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.StackedBar,
  name: '堆叠柱状图',
  description: '按维度堆叠、多指标堆叠、堆叠上叠加折线、图例位置',
  preview: stack,
  variants: [
    {
      key: 'stack',
      name: '按维度堆叠',
      note: 'seriesBy = 服务类型',
      card: stack,
    },
    {
      key: 'coded',
      name: '按编码值堆叠',
      note: 'seriesBy = 工单状态，行里是 finished 等代码，显示中文',
      card: byStatusCard,
    },
    {
      key: 'measures',
      name: '多指标堆叠',
      note: '配件 + 工时 = 总营收',
      card: {
        surface: 'dashboard',
        title: '月度营收构成',
        viz: {
          type: BiVizType.StackedBar,
          x: f.month.key,
          series: [{ key: f.partsRevenue.key }, { key: f.laborRevenue.key }],
          style: { dataLabels: true },
        },
        data: galleryReady(partsLaborResult),
      },
    },
    {
      key: 'stack-line',
      name: '堆叠 + 折线',
      note: '工单数为折线，画在右轴',
      card: {
        surface: 'dashboard',
        title: '营收构成与工单数',
        viz: {
          type: BiVizType.StackedBar,
          x: f.month.key,
          series: [
            { key: f.partsRevenue.key },
            { key: f.laborRevenue.key },
            {
              key: f.orders.key,
              as: BiSeriesType.Line,
              axis: BiAxisSide.Right,
            },
          ],
        },
        data: galleryReady(partsLaborResult),
      },
    },
    legendVariant(BiLegendPosition.Top),
    legendVariant(BiLegendPosition.Bottom),
    legendVariant(BiLegendPosition.Right),
    {
      key: 'legend-hidden',
      name: '隐藏图例',
      note: 'legend.show = false',
      card: {
        ...stack,
        viz: {
          ...stack.viz,
          style: {
            legend: { show: false, position: BiLegendPosition.Bottom },
          },
        },
      },
    },
  ],
};
